import { DynamicMapLayerOptions, dynamicMapLayer } from "esri-leaflet";
import { useCreateStore, useProvider } from "mobx-store-provider";
import { useEffect, useState } from "react";

import BaseMapDropdown from "./BaseMapDropdown";
import { CbrfcAPI } from "./models/CbrfcAPI";
import Desktop from "./Desktop";
import Disclaimer, { hosted } from "./Disclaimer";
import { JefdaqLegacyAPI } from "./models/JefdaqLegacyAPI";
import { Layers } from "./models/Layers";
import { Map as LeafletMap } from "leaflet";
import { LocaleContextProvider } from "./contexts/LocaleContext";
import Map from "./Map";
import { MapContainer } from "react-leaflet";
import { Menus } from "./models/Menus";
import Mobile from "./Mobile";
import Navbar from "./Navbar";
import { RadarContextProvider } from "./contexts/RadarContext";
import { WrfContextProvider } from "./contexts/WrfContext";
import Root from "./models/Root";
import { Sensors } from "./models/Sensors";
import { VaisalaAPI } from "./models/VaisalaAPI";
import config from "./config";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { useWindowSize } from "react-use";
import dynamicLayerMetadataLayers from "./layers/dynamic/dynamicLayerMetadata";

const App = () => {
  const sensorStore = useCreateStore(Sensors, {
    display: config.defaultDisplay,
  });

  const menuStore = useCreateStore(Menus);

  const layerStore = useCreateStore(Layers, {
    baseMap: config.basemap,
    static: config.layers.static,
    dynamic: config.layers.dynamic,
    misc: config.layers.misc,
  });

  const vaisalaApiStore = useCreateStore(VaisalaAPI);
  const cbrfcApiStore = useCreateStore(CbrfcAPI);

  const [mapCreateHandled, setMapCreateHandled] = useState(false);

  const [map, setMap] = useState<LeafletMap | undefined>(undefined);
  const mapCreated = (map_: LeafletMap) => {
    if (map_ === null || mapCreateHandled) {
      return;
    }

    setMap(map_);

    map_.attributionControl.addAttribution(
      '<a href="https://www.jefuller.com">JEFDAQ</a>'
    );

    layerStore.dynamic.forEach(({ url, visibleLayers, setMetadata }) => {
      const options: DynamicMapLayerOptions = { url, layers: [] };
      const mapLayer = dynamicMapLayer(options).addTo(map_);
      mapLayer.metadata((error, metadata) => {
        const layers = dynamicLayerMetadataLayers(error, metadata);
        if (!layers) {
          console.warn(`Unable to load dynamic layer metadata from ${url}`, error);
          return;
        }
        setMetadata(layers);
      });
      reaction(
        () => [visibleLayers, visibleLayers.length], // need length to trigger update
        (visibleLayers) => {
          mapLayer.setLayers(visibleLayers);
        }
      );
    });

    setMapCreateHandled(true);
  };

  const { height, width } = useWindowSize();
  useEffect(() => {
    const mapContainer = document.querySelector(".leaflet-container");
    if (mapContainer && map) {
      const navbarHeight = 60;
      (mapContainer as HTMLElement).style.height = `${height - navbarHeight}px`;
      map.invalidateSize();
    }
  }, [height, map]);

  const RootStoreProvider = useProvider(Root);
  const rootStore = useCreateStore(Root, {
    vaisalaApi: vaisalaApiStore,
    cbrfcApi: cbrfcApiStore,
    sensors: sensorStore,
    layers: layerStore,
    menus: menuStore,
    map: { zoom: config.zoom },
  });

  const LegacyAPIStoreProvider = useProvider(JefdaqLegacyAPI);
  const jefdaqLegacyApiStore = useCreateStore(
    JefdaqLegacyAPI,
    {
      precip: { loading: false, response: undefined },
      stage: { loading: false, response: undefined },
      rated: { loading: false, response: undefined },
    },
    { rootStore }
  );

  useEffect(() => {
    if (process.env.REACT_APP_LEGACY_API === "true") {
      jefdaqLegacyApiStore.fetch();
    } else {
      sensorStore.fetch();
    }
  }, [jefdaqLegacyApiStore, sensorStore]);

  useEffect(() => {
    config.layers.lightning && vaisalaApiStore.fetch();
  }, [vaisalaApiStore]);

  useEffect(() => {
    config.layers.cbrfc && cbrfcApiStore.fetch();
  }, [cbrfcApiStore]);
  layerStore.showCbrfc();

  const cameraParam = new URLSearchParams(window.location.search).get("camera");

  useEffect(() => {
    if (cameraParam) {
      layerStore.setCameraFromUrl(cameraParam);
    }
  }, [layerStore, cameraParam]);

  rootStore.updateWidth(width);
  const { isMobile } = rootStore;

  useEffect(() => {
    if (!isMobile && config.defaultMenuPanel && !cameraParam) {
      menuStore.openPanel(config.defaultMenuPanel);
    }
  }, [isMobile, cameraParam, menuStore]);

  let disclaimer;
  if (process.env.REACT_APP_HOSTED === "true") {
    disclaimer = hosted;
  } else {
    disclaimer = config.disclaimer;
  }

  return (
    <RootStoreProvider value={rootStore}>
      <LegacyAPIStoreProvider value={jefdaqLegacyApiStore}>
        <LocaleContextProvider>
          <RadarContextProvider>
            <WrfContextProvider>
              {disclaimer && (
                <Disclaimer outLink={disclaimer.outLink}>
                  {disclaimer.body}
                </Disclaimer>
              )}
              <Navbar />
              <MapContainer
                center={config.center}
                zoom={config.zoom}
                zoomControl={false}
                ref={mapCreated}
                className="sensors-loading"
              >
                <Map></Map>
                <BaseMapDropdown />
                {isMobile && <Mobile />}
                {!isMobile && <Desktop />}
              </MapContainer>
            </WrfContextProvider>
          </RadarContextProvider>
        </LocaleContextProvider>
      </LegacyAPIStoreProvider>
    </RootStoreProvider>
  );
};

export default observer(App);
